import { STORAGE_PREFIX } from '../config/app'

/**
 * **언어 공간** — 서버와 화면이 함께 보는 규칙 (docs/design-locale-spaces.md 4단계).
 *
 * 언어 하나 = 완전히 따로 떨어진 공간이다. 사주·내 사람들·기록·보관함·카드 모음·잠금이
 * 모두 공간 단위로 갈린다. **포인트 잔액만 공통**이다 (계정 하나에 하나).
 *
 * 이 파일은 서버 함수도 읽으므로 브라우저 것(`window`·`import.meta`)을 넣지 않는다.
 */

/**
 * 기기 저장소의 열쇠 이름. **언어마다 따로 담는다.**
 *
 *   saju.ko.results.v1 · saju.en.people.v1 …
 *
 * 로그인하지 않은 사람에게는 이것이 곧 공간이다 — 서버에 줄이 없으므로
 * 열쇠 이름으로만 갈린다.
 */
export function spaceKey(locale: string, name: string): string {
  return `${STORAGE_PREFIX}.${locale}.${name}.v1`
}

/** 언어가 붙기 전의 옛 열쇠 (`saju.results.v1`). 한 번 옮기고 표시를 남긴다. */
export function oldKey(name: string): string {
  return `${STORAGE_PREFIX}.${name}.v1`
}

/** 옛 열쇠를 옮겼다는 표시. 두 번 옮기지 않으려고 둔다. */
export const MOVED_KEY = `${STORAGE_PREFIX}.spaceMoved.v1`

/**
 * **궁합 잠금의 대상 값 — 만드는 곳은 여기 한 곳이다.**
 *
 * 화면과 서버가 서로 다른 값을 만들면 **깎이고도 안 열린다** (예전에 실제로
 * 40P 를 먹었다). 그래서 양쪽이 이 함수를 부른다.
 *
 * 공간이 앞에 붙는다. 예전에는 `{내 일주}|{상대 일주}|{관계}` 뿐이라 언어도 사주도
 * 모르는 값이었고, **한국어에서 산 궁합이 영어 공간에서 그냥 열렸다.**
 *
 * 끝에 **상대 사람의 번호**(`p:{사람 id}`)가 붙는다. 예전에는 사람이 없어
 * 김도현 궁합을 사면 일주·관계가 같은 박지민 궁합도 그냥 열렸다 (60명 중 1명꼴). 사람 번호는 사람을 담을 때 만든
 * 무작위 값(`newPersonId`)이고, 로그인하면 같은 값이 계정(`people.client_id`)으로 옮겨 가 바뀌지 않는다.
 * 같은 사람·같은 관계는 다시 열 때 0P, 다른 사람이거나 같은 사람의 다른 관계면 새로 산다.
 * 궁합 **글**(AI 캐시)은 그대로 두 일주·관계로 잡는다 — 값은 사람마다 받되 글은 함께 쓴다.
 */
export function compatTarget(spaceId: string | null, mine: string, other: string, kind: string, personId: string): string {
  const core = `${mine}|${other}|${kind}|p:${personId}`
  return spaceId ? `${spaceId}|${core}` : core
}

/**
 * **셋이 보는 일 궁합**(같이 일할 사람 둘)의 대상 값. 두 사람은 번호 순서로 놓아 누가 먼저 골랐든 같은 값이다 —
 * 같은 둘을 다시 열면 0P, 한 사람이라도 다르면 새로 산다. 화면과 서버가 이 한 함수를 쓴다.
 */
export function compatTeamTarget(spaceId: string | null, mine: string, x: { pillar: string; id: string }, y: { pillar: string; id: string }): string {
  const [p, q] = [x, y].sort((m, n) => (m.id < n.id ? -1 : m.id > n.id ? 1 : 0))
  const core = `${mine}|${p!.pillar}.${q!.pillar}|team|p:${p!.id}.${q!.id}`
  return spaceId ? `${spaceId}|${core}` : core
}

/** 사람 번호 — 사람을 담을 때 만든 값 (기기 16진수 16자 · 예전 값도 받는다) */
export function isPersonId(v: unknown): v is string {
  return typeof v === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(v)
}
